import { useEffect, useRef, useState, type PointerEvent, type WheelEvent } from "react";
import { Maximize2, RotateCcw, X, ZoomIn, ZoomOut } from "lucide-react";

const MIN = 1;
const MAX = 6;
const STEP = 0.5;

const clamp = (n: number) => Math.min(MAX, Math.max(MIN, n));

/**
 * The printed question behind a flag: the engine's crop of it from the book's
 * page (its own column, running on into the next if the question does).
 *
 * A thumbnail beside the issues; a tap opens it over the whole screen, where
 * a reviewer can zoom in (buttons, +/−, the mouse wheel or a trackpad pinch)
 * and drag it about to read small print. A click outside it, the ✕ or Escape
 * closes it back to the thumbnail.
 */
export function PageImage({ src, label }: { src: string; label: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`Open ${label} full screen`}
        className="group relative block w-full overflow-hidden rounded-lg border border-border bg-white"
      >
        <img src={src} alt={label} className="block max-h-72 w-full object-contain" />
        <span className="absolute right-2 top-2 inline-flex items-center gap-1 rounded bg-bg/80 px-2 py-1 text-[11px] font-semibold text-text opacity-90 group-hover:opacity-100">
          <Maximize2 size={12} aria-hidden="true" /> View full screen
        </span>
      </button>
      {open ? <Viewer src={src} label={label} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function Viewer({ src, label, onClose }: { src: string; label: string; onClose: () => void }) {
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const drag = useRef<{ x: number; y: number; ox: number; oy: number; moved: boolean } | null>(
    null,
  );
  const closeButton = useRef<HTMLButtonElement>(null);

  const zoomTo = (next: number) => {
    const z = clamp(next);
    setZoom(z);
    // Back at full size there is nothing to pan: re-centre.
    if (z === MIN) setOffset({ x: 0, y: 0 });
  };

  useEffect(() => {
    closeButton.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "+" || e.key === "=") setZoom((z) => clamp(z + STEP));
      else if (e.key === "-") {
        setZoom((z) => {
          const next = clamp(z - STEP);
          if (next === MIN) setOffset({ x: 0, y: 0 });
          return next;
        });
      } else if (e.key === "0") {
        setZoom(MIN);
        setOffset({ x: 0, y: 0 });
      }
    };
    window.addEventListener("keydown", onKey);
    // The page behind must not scroll while the viewer is up.
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
    };
  }, [onClose]);

  const onWheel = (e: WheelEvent) => {
    // A trackpad pinch arrives as a wheel event with ctrlKey set.
    zoomTo(zoom * (e.deltaY < 0 ? 1.15 : 1 / 1.15));
  };

  const onPointerDown = (e: PointerEvent) => {
    if (zoom === MIN) return;
    drag.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y, moved: false };
    (e.target as Element).setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e: PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    if (Math.abs(dx) + Math.abs(dy) > 3) d.moved = true;
    setOffset({ x: d.ox + dx, y: d.oy + dy });
  };
  const onPointerUp = () => {
    drag.current = null;
  };

  const control =
    "inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-surface text-text disabled:opacity-40";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={label}
      className="fixed inset-0 z-50 flex flex-col bg-black/90"
    >
      {/* A click on the dark backdrop closes; on the picture or a control it does not. */}
      <div
        className="flex items-center justify-between gap-2 px-4 py-3"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <p className="truncate text-[13px] text-white/80">{label}</p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label="Zoom out"
            disabled={zoom <= MIN}
            onClick={() => zoomTo(zoom - STEP)}
            className={control}
          >
            <ZoomOut size={16} aria-hidden="true" />
          </button>
          <span className="w-12 text-center text-[12px] text-white tabular-nums" aria-live="polite">
            {Math.round(zoom * 100)}%
          </span>
          <button
            type="button"
            aria-label="Zoom in"
            disabled={zoom >= MAX}
            onClick={() => zoomTo(zoom + STEP)}
            className={control}
          >
            <ZoomIn size={16} aria-hidden="true" />
          </button>
          <button
            type="button"
            aria-label="Reset zoom"
            disabled={zoom === MIN}
            onClick={() => zoomTo(MIN)}
            className={control}
          >
            <RotateCcw size={16} aria-hidden="true" />
          </button>
          <button
            ref={closeButton}
            type="button"
            aria-label="Close"
            onClick={onClose}
            className={`${control} ml-2`}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>
      </div>
      <div
        data-testid="page-image-stage"
        className="flex flex-1 items-center justify-center overflow-hidden"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
        onWheel={onWheel}
      >
        <img
          src={src}
          alt={label}
          draggable={false}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onDoubleClick={() => zoomTo(zoom === MIN ? 2.5 : MIN)}
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
            cursor: zoom > MIN ? "grab" : "zoom-in",
          }}
          className="max-h-full max-w-full select-none bg-white object-contain transition-transform duration-75"
        />
      </div>
      <p className="pb-3 text-center text-[11px] text-white/60">
        Scroll or pinch to zoom · drag to move · double-click to zoom · Esc or click outside to
        close
      </p>
    </div>
  );
}
